import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ASK_WIDTH,
  isAskOverlayViewport,
  loadAskPrefs,
  storeAskPrefs,
  trackAsk,
} from "./ask";

const AskContext = createContext(null);

export const useAsk = () => useContext(AskContext) || { enabled: false };

export const AskProvider = ({
  children,
  enabled = false,
  shopName = "",
  isPlatformAdmin = false,
}) => {
  const prefs = loadAskPrefs();
  const [open, setOpen] = useState(!!prefs.open);
  const [wide, setWideState] = useState(!!prefs.wide);
  const [overlay, setOverlay] = useState(
    typeof window !== "undefined" ? isAskOverlayViewport() : false
  );
  const [prefill, setPrefill] = useState("");
  const [autoSend, setAutoSend] = useState(false);
  const [pin, setPin] = useState(null);
  const [threadKey, setThreadKey] = useState(0);
  const composerRef = useRef(null);
  const lastSourceRef = useRef("topbar");

  useEffect(() => {
    const onResize = () => setOverlay(isAskOverlayViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const persist = useCallback((next) => {
    storeAskPrefs(next);
  }, []);

  const focusComposer = useCallback(() => {
    requestAnimationFrame(() => {
      composerRef.current?.focus();
    });
  }, []);

  const startFreshThread = useCallback(() => {
    setPrefill("");
    setAutoSend(false);
    setPin(null);
    setThreadKey((n) => n + 1);
  }, []);

  const openAsk = useCallback(
    ({
      source = "topbar",
      question = "",
      pin: nextPin = null,
      send = false,
    } = {}) => {
      if (!enabled) return;
      lastSourceRef.current = source;
      const wasOpen = open;
      const q = String(question || "").trim();
      if (!wasOpen || (q && send)) {
        setThreadKey((n) => n + 1);
        if (!wasOpen) trackAsk("ask_opened", { source });
      }
      setPrefill(q);
      setAutoSend(!!(q && send));
      setPin(nextPin || null);
      setOpen(true);
      persist({ open: true, wide });
      focusComposer();
    },
    [enabled, open, wide, persist, focusComposer]
  );

  const closeAsk = useCallback(() => {
    setOpen(false);
    persist({ open: false, wide });
  }, [persist, wide]);

  const toggleAsk = useCallback(
    (source = "topbar") => {
      if (open) closeAsk();
      else openAsk({ source });
    },
    [open, closeAsk, openAsk]
  );

  const setWide = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(wide) : !!next;
      setWideState(value);
      persist({ open, wide: value });
    },
    [open, wide, persist]
  );

  const newConversation = useCallback(() => {
    startFreshThread();
    focusComposer();
  }, [startFreshThread, focusComposer]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        toggleAsk("shortcut");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [enabled, toggleAsk]);

  useEffect(() => {
    const width = wide ? ASK_WIDTH.wide : ASK_WIDTH.default;
    document.body.style.setProperty("--ask-panel-width", `${width}px`);
    document.body.classList.toggle("riverr-ask-open", !!(enabled && open));
    document.body.classList.toggle(
      "riverr-ask-docked",
      !!(enabled && open && !overlay)
    );
    document.body.classList.toggle(
      "riverr-ask-overlay",
      !!(enabled && open && overlay)
    );
    return () => {
      document.body.classList.remove(
        "riverr-ask-open",
        "riverr-ask-docked",
        "riverr-ask-overlay"
      );
    };
  }, [enabled, open, wide, overlay]);

  const value = useMemo(
    () => ({
      enabled,
      open,
      wide,
      overlay,
      shopName,
      isPlatformAdmin,
      prefill,
      autoSend,
      pin,
      threadKey,
      composerRef,
      widthPx: wide ? ASK_WIDTH.wide : ASK_WIDTH.default,
      openAsk,
      closeAsk,
      toggleAsk,
      setWide,
      newConversation,
      consumePrefill: () => {
        const q = prefill;
        setPrefill("");
        return q;
      },
      consumeAutoSend: () => {
        const next = autoSend;
        setAutoSend(false);
        return next;
      },
    }),
    [
      enabled,
      open,
      wide,
      overlay,
      shopName,
      isPlatformAdmin,
      prefill,
      autoSend,
      pin,
      threadKey,
      openAsk,
      closeAsk,
      toggleAsk,
      setWide,
      newConversation,
    ]
  );

  return <AskContext.Provider value={value}>{children}</AskContext.Provider>;
};
